import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as ShippingIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  Store as StoreIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import purchaseStatsService from '../services/purchaseStatsService';
import suppliersService from '../services/suppliersService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const PurchaseStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    supplierId: '',
  });

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    loadStats();
  }, [filters]);

  const loadSuppliers = async () => {
    try {
      const response = await suppliersService.getAllSuppliers();
      setSuppliers(Array.isArray(response) ? response : response.data || []);
    } catch (err) {
      console.error('Erreur chargement fournisseurs:', err);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchaseStatsService.getStats(filters);
      setStats(response.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('fr-FR').format(value || 0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters({
      ...filters,
      [field]: event.target.value,
    });
  };

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  const { general, byStatus, bySupplier, byMonth, topProducts, last12Months, byDayOfWeek, recentOrders } = stats;

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          📊 Statistiques d'Achat
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            type="date"
            label="Date de début"
            value={filters.startDate}
            onChange={handleFilterChange('startDate')}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            type="date"
            label="Date de fin"
            value={filters.endDate}
            onChange={handleFilterChange('endDate')}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Fournisseur</InputLabel>
            <Select
              value={filters.supplierId}
              onChange={handleFilterChange('supplierId')}
              label="Fournisseur"
            >
              <MenuItem value="">Tous les fournisseurs</MenuItem>
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Statistiques principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'primary.dark' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total des Achats
                  </Typography>
                  <Typography variant="h4">{formatCurrency(general.totalAmount)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {general.totalOrders} commande{general.totalOrders > 1 ? 's' : ''}
                  </Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'success.dark' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Panier Moyen
                  </Typography>
                  <Typography variant="h4">{formatCurrency(general.avgOrderAmount)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Par commande
                  </Typography>
                </Box>
                <ShoppingCartIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'warning.dark' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Frais de Livraison
                  </Typography>
                  <Typography variant="h4">{formatCurrency(general.totalShippingCost)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {((general.totalShippingCost / general.totalAmount) * 100).toFixed(1)}% du total
                  </Typography>
                </Box>
                <ShippingIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', bgcolor: 'info.dark' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Quantité Totale
                  </Typography>
                  <Typography variant="h4">{formatNumber(general.totalItemsQuantity)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Articles achetés
                  </Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Graphiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Évolution sur 12 mois */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Évolution des Achats (12 derniers mois)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={last12Months}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'Nombre de commandes') {
                        return formatNumber(value);
                      }
                      return formatCurrency(value);
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="totalAmount" stroke="#8884d8" name="Montant total" />
                  <Line type="monotone" dataKey="orderCount" stroke="#82ca9d" name="Nombre de commandes" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Répartition par statut */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition par Statut
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="total"
                  >
                    {byStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistiques par mois */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistiques par Mois
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={byMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'Nombre de commandes') {
                        return formatNumber(value);
                      }
                      return formatCurrency(value);
                    }}
                  />
                  <Legend />
                  <Bar dataKey="totalAmount" fill="#8884d8" name="Montant total" />
                  <Bar dataKey="orderCount" fill="#82ca9d" name="Nombre de commandes" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tableaux */}
      <Grid container spacing={3}>
        {/* Top Fournisseurs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Fournisseurs
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fournisseur</TableCell>
                      <TableCell align="right">Commandes</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Moyenne</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bySupplier.slice(0, 10).map((supplier) => (
                      <TableRow key={supplier.supplierId}>
                        <TableCell>{supplier.supplierName}</TableCell>
                        <TableCell align="right">{supplier.orderCount}</TableCell>
                        <TableCell align="right">{formatCurrency(supplier.totalAmount)}</TableCell>
                        <TableCell align="right">{formatCurrency(supplier.avgAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Produits */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Produits Achetés
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Prix Moyen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.map((product) => (
                      <TableRow key={product.productId}>
                        <TableCell>{product.productName}</TableCell>
                        <TableCell align="right">{formatNumber(product.totalQuantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(product.totalAmount)}</TableCell>
                        <TableCell align="right">{formatCurrency(product.avgUnitPrice)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Commandes récentes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Commandes Récentes
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Fournisseur</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell align="right">Montant</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>#{order.id}</TableCell>
                        <TableCell>{order.supplierName}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            color={
                              order.status === 'received'
                                ? 'success'
                                : order.status === 'ordered'
                                ? 'info'
                                : order.status === 'cancelled'
                                ? 'error'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">{formatCurrency(order.totalAmount)}</TableCell>
                        <TableCell>
                          {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Détails supplémentaires */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Détails Financiers
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Total des items:</Typography>
                  <Typography fontWeight="bold">{formatCurrency(general.itemsTotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Frais de livraison:</Typography>
                  <Typography fontWeight="bold">{formatCurrency(general.totalShippingCost)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Taxes:</Typography>
                  <Typography fontWeight="bold">{formatCurrency(general.totalTaxAmount)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Frais supplémentaires:</Typography>
                  <Typography fontWeight="bold">{formatCurrency(general.totalAdditionalFees)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h6">Total général:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatCurrency(general.totalAmount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Répartition par Jour de la Semaine
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Jour</TableCell>
                      <TableCell align="right">Commandes</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byDayOfWeek.map((day) => (
                      <TableRow key={day.dayOfWeek}>
                        <TableCell>{day.dayName}</TableCell>
                        <TableCell align="right">{day.orderCount}</TableCell>
                        <TableCell align="right">{formatCurrency(day.totalAmount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PurchaseStats;
